/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logDemoCustomContent } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedNERResult } from 'src/languageStudio/appShell/prebuiltDemos/components/VisualizedNERResult';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { labelDescriptionIconClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { textAnalyticsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    piiApiReferenceLink,
    piiDocLink,
    piiOverviewLink,
    piiSamplesLink,
    piiSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { Language, piiDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { PIIDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import {
    isDocumentSwaggerRtl,
    isDocumentSwaggerWordTokenized,
    validateLanguageStudioResource
} from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.piiDemoPage.pageHeader', defaultMessage: 'Extract PII' },
    capabilityDescription: {
        id: 'languageStudio.piiDemoPage.capabilityDescription',
        defaultMessage: `This prebuilt capability uses Named Entity Recognition (NER) to identify sensitive
        personally identifiable information (PII) in text and categorize them into pre-defined classes or
        types such as: person, address, email, phone number, passport number, bank account number, etc.`
    },
    emptyState: {
        id: 'languageStudio.piiDemoPage.emptyState',
        defaultMessage: `Your results will appear here once you enter or upload some text and press Run.
        The file format needs to be .txt`
    },
    healthInfoOptionLabel: {
        id: 'languageStudio.piiDemoPage.healthInfoOptionLabel',
        defaultMessage: 'Extract Protected Health Information'
    },
    healthInfoDescription: {
        id: 'languageStudio.piiDemoPage.healthInfoDescription',
        defaultMessage: 'This is a subset of PII types specifically filtered for PHI.'
    },
    piiEntitiesIdentifiedTitle: {
        id: 'languageStudio.piiDemoPage.piiEntitiesIdentifiedTitle',
        defaultMessage: 'Personally Identifiable Information (PII) and Protected Health Information (PHI)'
    },
    hideCardsMessage: {
        id: 'languageStudio.piiDemoPage.hideCardsMessage',
        defaultMessage: 'Turn on cards to see PII and PHI entities'
    }
});

/**
 * Displays the PII demo page.
 */
export const PIIDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isMediumOrGreater } = useResponsiveMode();

    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore } = rootStore.azureStore.session;

    const { tracker, resetResults, getCurlCommand, piiResult, getPII, detectedLanguages } = useTextAnalyticsCapabilities();

    const [document, setDocument] = React.useState('');
    const [withHealthInfo, setWithHealthInfo] = React.useState(false);
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const piiOptions = (
        <Stack styles={{ root: { flex: 2 } }} tokens={{ childrenGap: 17 }}>
            <LuisText fontWeight={500}>{localize(commonDemosMessages.options)}</LuisText>

            <Toggle
                inlineLabel={isMediumOrGreater}
                checked={withHealthInfo}
                onText={localize(commonDemosMessages.onText)}
                offText={localize(commonDemosMessages.offText)}
                onChange={(_, isChecked) => setWithHealthInfo(isChecked)}
                disabled={isSignedIn && !validateLanguageStudioResource({ resource: csAccountStore, localize }).isValid}
                label={
                    <Stack horizontal verticalAlign="center" tokens={{ padding: '0 0 5px 0' }}>
                        <LuisText variant="medium" fontWeight={500} styles={{ root: { whiteSpace: 'nowrap' } }}>
                            {localize(messages.healthInfoOptionLabel)}
                        </LuisText>
                        <CalloutButton
                            isDarkMode={false}
                            iconClassName={labelDescriptionIconClassName}
                            content={localize(messages.healthInfoDescription)}
                        />
                    </Stack>
                }
            />
        </Stack>
    );

    const handleDocumentChange = (newDocument: string) => {
        if (piiResult) {
            resetResults();
        }
        setDocument(newDocument);
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent('Extract PII');
        }

        getPII({
            document,
            language,
            withPhi: withHealthInfo,
            sampleTitle: selectedSampleTitle,
            autoDetectLanguage: languageAutoDetection
        });
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                getSdkLink={piiSdkLink}
                featureName="Extract PII"
                supportedPlatforms={['cloud']}
                documentationLink={piiDocLink}
                learnMoreLink={piiOverviewLink}
                gitHubSamplesLink={piiSamplesLink}
                demoTitle={localize(messages.pageHeader)}
                demoVersion={LuisConstants.Demos.ApiVersions.GA}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                featureName="Extract PII"
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                acceptedResourceType="TextAnalytics"
                samplesList={textAnalyticsDemoSamples}
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                supportedRegionLink={piiApiReferenceLink}
                languagesList={piiDemoSupportedLanguages}
                onRenderHorizontalOptions={() => piiOptions}
                containerStackProps={{ tokens: { maxWidth: '100%' } }}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                isTextRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}>
                <DemoPageAgreement
                    featureName="Extract PII"
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={!document.length}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<PIIDocuments>
                results={piiResult}
                featureName="Extract PII"
                isLoading={tracker.isRunning}
                emptyStateMessage={localize(messages.emptyState)}>
                <VisualizedNERResult
                    showRedactionToggle
                    document={document}
                    featureName="Extract PII"
                    detectedLanguage={detectedLanguages}
                    entities={piiResult?.documents[0].entities}
                    redactedDocument={piiResult?.documents[0].redactedText}
                    hiddenEntitiesMessage={localize(messages.hideCardsMessage)}
                    entitiesSectionTitle={localize(messages.piiEntitiesIdentifiedTitle)}
                    isLabelerRTL={isDocumentSwaggerRtl(detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language)}
                    isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                        detectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                    )}
                />
            </ResultsStep>

            <NextStepsStep
                sdkLink={piiSdkLink}
                featureName="Extract PII"
                isResultsEmpty={!piiResult}
                documentationLink={piiDocLink}
                sampleCodeLink={piiSamplesLink}
                demosToDisplay={['ner', 'keyPhrases', 'taForHealth']}
                curlCommand={getCurlCommand({
                    language,
                    text: document,
                    languageAutoDetection,
                    endpoint: 'PiiEntityRecognition',
                    parameters: { domain: withHealthInfo ? 'phi' : 'none' }
                })}
            />
        </Stack>
    );
});
